/**
 * Assessment Data Handler
 * Handles assessment data retrieval and merging from database and localStorage
 */

/**
 * Merges assessment data from database with extended data from localStorage
 * @param {Array} assessments - Assessment data from database
 * @returns {Array} Enhanced assessment data with localStorage data merged
 */
export function mergeAssessmentData(assessments) {
  if (!assessments || !Array.isArray(assessments)) {
    return []
  }

  return assessments.map(assessment => {
    // Try to get extended data from localStorage
    const extendedDataKey = `assessment_${assessment.id}_extended`
    const storedExtendedData = localStorage.getItem(extendedDataKey)
    
    if (storedExtendedData) {
      try {
        const extendedData = JSON.parse(storedExtendedData)
        
        // Merge the data, prioritizing database data over localStorage
        return {
          ...assessment,
          total_questions: assessment.total_questions ?? extendedData.totalQuestions ?? 0,
          answered_questions: assessment.answered_questions ?? extendedData.answeredQuestions ?? 0,
          responses: assessment.responses ?? extendedData.responses ?? {},
          recommendations: assessment.recommendations ?? extendedData.recommendations ?? [],
          // Add a flag to indicate this data was enhanced from localStorage
          _enhanced_from_storage: true
        }
      } catch (error) {
        console.warn(`Failed to parse extended data for assessment ${assessment.id}:`, error)
      }
    }
    
    // Return original assessment if no localStorage data or parsing failed
    return {
      ...assessment,
      total_questions: assessment.total_questions ?? 0,
      answered_questions: assessment.answered_questions ?? 0,
      responses: assessment.responses ?? {},
      recommendations: assessment.recommendations ?? []
    }
  })
}

/**
 * Gets assessment statistics from enhanced data
 * @param {Array} assessments - Enhanced assessment data
 * @returns {Object} Statistics object
 */
export function getAssessmentStats(assessments) {
  if (!assessments || assessments.length === 0) {
    return {
      totalAssessments: 0,
      averageScore: 0,
      totalQuestions: 0,
      totalAnswered: 0,
      completionRate: 0,
      riskDistribution: { low: 0, medium: 0, high: 0 }
    }
  }

  const totalAssessments = assessments.length
  const totalScore = assessments.reduce((sum, a) => sum + (a.score || 0), 0)
  const averageScore = totalScore / totalAssessments

  const totalQuestions = assessments.reduce((sum, a) => sum + (a.total_questions || 0), 0)
  const totalAnswered = assessments.reduce((sum, a) => sum + (a.answered_questions || 0), 0)
  const completionRate = totalQuestions > 0 ? (totalAnswered / totalQuestions) * 100 : 0

  const riskDistribution = assessments.reduce((dist, a) => {
    const risk = a.risk_level || 'medium'
    dist[risk] = (dist[risk] || 0) + 1
    return dist
  }, { low: 0, medium: 0, high: 0 })

  return {
    totalAssessments,
    averageScore: Math.round(averageScore * 100) / 100,
    totalQuestions,
    totalAnswered,
    completionRate: Math.round(completionRate * 100) / 100,
    riskDistribution
  }
}

/**
 * Cleans up old localStorage assessment data
 * @param {number} maxAgeMs - Maximum age in milliseconds (default: 30 days)
 */
export function cleanupOldAssessmentData(maxAgeMs = 30 * 24 * 60 * 60 * 1000) {
  const now = Date.now()
  const keysToRemove = []

  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i)
    if (key && key.startsWith('assessment_') && key.endsWith('_extended')) {
      try {
        const data = JSON.parse(localStorage.getItem(key))
        const dataAge = now - new Date(data.timestamp).getTime()
        
        if (dataAge > maxAgeMs) {
          keysToRemove.push(key)
        }
      } catch (error) {
        // If we can't parse the data, it's probably corrupted, so remove it
        keysToRemove.push(key)
      }
    }
  }

  keysToRemove.forEach(key => {
    localStorage.removeItem(key)
    console.log(`Cleaned up old assessment data: ${key}`)
  })

  if (keysToRemove.length > 0) {
    console.log(`Cleaned up ${keysToRemove.length} old assessment data entries`)
  }
}

/**
 * Gets the display value for assessment data, handling missing columns gracefully
 * @param {Object} assessment - Assessment object
 * @param {string} field - Field name to get
 * @param {*} defaultValue - Default value if field is missing
 * @returns {*} Field value or default
 */
export function getAssessmentField(assessment, field, defaultValue = 'N/A') {
  if (!assessment) return defaultValue
  
  const value = assessment[field]
  
  // Handle specific field types
  switch (field) {
    case 'total_questions':
    case 'answered_questions':
      return typeof value === 'number' ? value : (defaultValue === 'N/A' ? 0 : defaultValue)
    
    case 'responses':
      return value && typeof value === 'object' ? value : {}
    
    case 'recommendations':
      return Array.isArray(value) ? value : []
    
    case 'score':
      return typeof value === 'number' ? Math.round(value) : 0
    
    default:
      return value !== undefined && value !== null ? value : defaultValue
  }
}